/**
 * Addressbook Redux Sagas
 * Handles async operations for addressbook domain
 *
 * Features:
 * - Toast notifications for CRUD success/failure
 * - Proper error extraction from API responses
 * - Refresh patterns after mutations
 */

import { call, put, takeLatest, all, type SagaReturnType } from 'redux-saga/effects'
import type { PayloadAction } from '@reduxjs/toolkit'
import { extractErrorMessage, showSuccessToast, showErrorToast } from '@ufc/core'
import { addressbookActions } from './addressbook.slice'
import { addressbookApi, countriesApi, citiesApi, regionsApi } from '../infrastructure/api/addressbook-api'
import type {
  AddressesFilter,
  CreateAddressRequest,
  UpdateAddressRequest,
} from '../domain/types'

// ============================================================================
// Toast Messages (i18n keys)
// ============================================================================

const TOAST = {
  SUCCESS: {
    TITLE: 'toast.success',
    CREATED: 'toast.addresses.created',
    UPDATED: 'toast.addresses.updated',
    DELETED: 'toast.addresses.deleted',
    FAVORITE_ADDED: 'toast.addresses.favoriteAdded',
    FAVORITE_REMOVED: 'toast.addresses.favoriteRemoved',
    SET_DEFAULT: 'toast.addresses.setDefault',
  },
  ERROR: {
    TITLE: 'toast.error',
    FETCH_FAILED: 'toast.addresses.fetchFailed',
    CREATE_FAILED: 'toast.addresses.createFailed',
    UPDATE_FAILED: 'toast.addresses.updateFailed',
    DELETE_FAILED: 'toast.addresses.deleteFailed',
  },
} as const

// ============================================================================
// Fetch Addresses Saga
// ============================================================================

function* fetchAddressesSaga(action: PayloadAction<AddressesFilter | undefined>) {
  try {
    const result: SagaReturnType<typeof addressbookApi.getAddressesPaginated> = yield call(
      addressbookApi.getAddressesPaginated,
      action.payload
    )
    yield put(addressbookActions.fetchAddressesSuccess(result))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch addresses'
    yield put(addressbookActions.fetchAddressesFailure(message))
  }
}

// ============================================================================
// Fetch Single Address Saga
// ============================================================================

function* fetchAddressSaga(action: PayloadAction<string>) {
  try {
    const address: SagaReturnType<typeof addressbookApi.getAddress> = yield call(
      addressbookApi.getAddress,
      action.payload
    )
    if (address) {
      yield put(addressbookActions.fetchAddressSuccess(address))
    } else {
      yield put(addressbookActions.fetchAddressFailure('Address not found'))
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch address'
    yield put(addressbookActions.fetchAddressFailure(message))
  }
}

// ============================================================================
// Fetch Stats Saga
// ============================================================================

function* fetchStatsSaga() {
  try {
    const stats: SagaReturnType<typeof addressbookApi.getAddressStats> = yield call(
      addressbookApi.getAddressStats
    )
    yield put(addressbookActions.fetchStatsSuccess(stats))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch stats'
    yield put(addressbookActions.fetchStatsFailure(message))
  }
}

// ============================================================================
// Fetch Location Types Saga
// ============================================================================

function* fetchLocationTypesSaga() {
  try {
    const locationTypes: SagaReturnType<typeof addressbookApi.getLocationTypes> = yield call(
      addressbookApi.getLocationTypes
    )
    yield put(addressbookActions.fetchLocationTypesSuccess(locationTypes))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch location types'
    yield put(addressbookActions.fetchLocationTypesFailure(message))
  }
}

// ============================================================================
// Fetch Countries Saga
// ============================================================================

function* fetchCountriesSaga() {
  try {
    const countries: SagaReturnType<typeof countriesApi.getAll> = yield call(
      countriesApi.getAll
    )
    yield put(addressbookActions.fetchCountriesSuccess(countries))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch countries'
    yield put(addressbookActions.fetchCountriesFailure(message))
  }
}

// ============================================================================
// Fetch Cities Saga
// ============================================================================

function* fetchCitiesSaga(action: PayloadAction<string | undefined>) {
  try {
    const cities: any[] = action.payload 
      ? yield call(citiesApi.getByCountry, action.payload)
      : yield call(citiesApi.getAll)
    yield put(addressbookActions.fetchCitiesSuccess(cities))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch cities'
    yield put(addressbookActions.fetchCitiesFailure(message))
  }
}

// ============================================================================
// Fetch Regions Saga
// ============================================================================

function* fetchRegionsSaga() {
  try {
    const regions: any[] = yield call(regionsApi.getAll)
    yield put(addressbookActions.fetchRegionsSuccess(regions))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch regions'
    yield put(addressbookActions.fetchRegionsFailure(message))
  }
}

// ============================================================================
// Create Address Saga
// ============================================================================

function* createAddressSaga(action: PayloadAction<CreateAddressRequest>) {
  try {
    const address: SagaReturnType<typeof addressbookApi.createAddress> = yield call(
      addressbookApi.createAddress,
      action.payload
    )
    yield put(addressbookActions.createAddressSuccess(address))
    // Refresh stats after creating
    yield put(addressbookActions.fetchStats())
    // Show success toast
    showSuccessToast(TOAST.SUCCESS.TITLE, TOAST.SUCCESS.CREATED)
  } catch (error) {
    const message = extractErrorMessage(error, 'Failed to create address')
    yield put(addressbookActions.createAddressFailure(message))
    // Show error toast
    showErrorToast(TOAST.ERROR.TITLE, message)
  }
}

// ============================================================================
// Update Address Saga
// ============================================================================

function* updateAddressSaga(
  action: PayloadAction<{ id: string; documentId: string; data: UpdateAddressRequest }>
) {
  try {
    const { documentId, data } = action.payload
    const address: SagaReturnType<typeof addressbookApi.updateAddress> = yield call(
      addressbookApi.updateAddress,
      documentId,
      data
    )
    yield put(addressbookActions.updateAddressSuccess(address))
    // Show success toast
    showSuccessToast(TOAST.SUCCESS.TITLE, TOAST.SUCCESS.UPDATED)
  } catch (error) {
    const message = extractErrorMessage(error, 'Failed to update address')
    yield put(addressbookActions.updateAddressFailure(message))
    // Show error toast
    showErrorToast(TOAST.ERROR.TITLE, message)
  }
}

// ============================================================================
// Delete Address Saga
// ============================================================================

function* deleteAddressSaga(action: PayloadAction<{ id: string; documentId: string }>) {
  try {
    const success: SagaReturnType<typeof addressbookApi.deleteAddress> = yield call(
      addressbookApi.deleteAddress,
      action.payload.documentId
    )
    if (success) {
      yield put(addressbookActions.deleteAddressSuccess(action.payload.id))
      // Refresh stats after deleting
      yield put(addressbookActions.fetchStats())
      // Show success toast
      showSuccessToast(TOAST.SUCCESS.TITLE, TOAST.SUCCESS.DELETED)
    } else {
      const message = 'Failed to delete address'
      yield put(addressbookActions.deleteAddressFailure(message))
      showErrorToast(TOAST.ERROR.TITLE, message)
    }
  } catch (error) {
    const message = extractErrorMessage(error, 'Failed to delete address')
    yield put(addressbookActions.deleteAddressFailure(message))
    // Show error toast
    showErrorToast(TOAST.ERROR.TITLE, message)
  }
}

// ============================================================================
// Toggle Favorite Saga
// ============================================================================

function* toggleFavoriteSaga(action: PayloadAction<{ id: string; documentId: string }>) {
  try {
    const address: SagaReturnType<typeof addressbookApi.toggleFavorite> = yield call(
      addressbookApi.toggleFavorite,
      action.payload.documentId
    )
    yield put(addressbookActions.toggleFavoriteSuccess(address))
    // Show success toast based on new favorite state
    const toastMessage = address.isFavorite
      ? TOAST.SUCCESS.FAVORITE_ADDED
      : TOAST.SUCCESS.FAVORITE_REMOVED
    showSuccessToast(TOAST.SUCCESS.TITLE, toastMessage)
  } catch (error) {
    const message = extractErrorMessage(error, 'Failed to toggle favorite')
    yield put(addressbookActions.toggleFavoriteFailure(message))
    showErrorToast(TOAST.ERROR.TITLE, message)
  }
}

// ============================================================================
// Set Default Saga
// ============================================================================

function* setDefaultSaga(action: PayloadAction<{ id: string; documentId: string }>) {
  try {
    const address: SagaReturnType<typeof addressbookApi.setDefault> = yield call(
      addressbookApi.setDefault,
      action.payload.documentId
    )
    yield put(addressbookActions.setDefaultSuccess(address))
    // Show success toast
    showSuccessToast(TOAST.SUCCESS.TITLE, TOAST.SUCCESS.SET_DEFAULT)
  } catch (error) {
    const message = extractErrorMessage(error, 'Failed to set default address')
    yield put(addressbookActions.setDefaultFailure(message))
    showErrorToast(TOAST.ERROR.TITLE, message)
  }
}

// ============================================================================
// Filter Change Saga
// ============================================================================

function* filterChangeSaga(action: PayloadAction<AddressesFilter['type'] | string>) {
  // When filter changes, refetch addresses with new filter
  // We need to combine with existing filter state
  // For now, we'll just trigger a refetch - the component should dispatch fetchAddresses with the current filter
  yield put(addressbookActions.setLoading(false))
}

// ============================================================================
// Root Saga
// ============================================================================

export function* addressbookSaga() {
  yield all([
    takeLatest(addressbookActions.fetchAddresses.type, fetchAddressesSaga),
    takeLatest(addressbookActions.fetchAddress.type, fetchAddressSaga),
    takeLatest(addressbookActions.fetchStats.type, fetchStatsSaga),
    takeLatest(addressbookActions.fetchLocationTypes.type, fetchLocationTypesSaga),
    takeLatest(addressbookActions.fetchCountries.type, fetchCountriesSaga),
    takeLatest(addressbookActions.fetchCities.type, fetchCitiesSaga),
    takeLatest(addressbookActions.fetchRegions.type, fetchRegionsSaga),
    takeLatest(addressbookActions.createAddress.type, createAddressSaga),
    takeLatest(addressbookActions.updateAddress.type, updateAddressSaga),
    takeLatest(addressbookActions.deleteAddress.type, deleteAddressSaga),
    takeLatest(addressbookActions.toggleFavorite.type, toggleFavoriteSaga),
    takeLatest(addressbookActions.setDefault.type, setDefaultSaga),
    takeLatest(addressbookActions.filterByType.type, filterChangeSaga),
    takeLatest(addressbookActions.filterByStatus.type, filterChangeSaga),
    takeLatest(addressbookActions.searchAddresses.type, filterChangeSaga),
  ])
}
